import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useAuthContext } from '../context/authContext';
import { useOfflineModeContext } from '../context/offlineContext';
import { useOfflineStatusContext } from '../context/offlineStatusContext';
import { emptyState, transact, queueClose } from './store';
import { NotificationService } from './service';
import { setNotificationBridge } from './bridge';
import { notificationManager } from '../utils/notificationManager';

const Context = createContext({ enabled: false, ready: false, state: emptyState() });
export const useNotifications = () => useContext(Context);
export function NotificationsProvider({ children }) {
  const client = useApolloClient();
  const { authUser, deviceRegistryStatus } = useAuthContext();
  const { networkState } = useOfflineModeContext();
  const online = Boolean(networkState && networkState.online);
  const { refreshStatus } = useOfflineStatusContext();
  const blocked = (deviceRegistryStatus?.results || deviceRegistryStatus)?.accessAllowed === false;
  const scope = authUser?.id && !blocked && process.env.REACT_APP_NOTIFICATIONS_ENABLED === 'true'
    ? JSON.stringify([process.env.REACT_APP_GRAPHQL_SERVER, process.env.REACT_APP_CLIENT, authUser.uid || authUser.firebase_uid, String(authUser.id), authUser.organization]) : null;
  const [view, setView] = useState({ scope: null, state: emptyState(), ready: false, enabled: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const current = useRef({});
  current.current = { scope, online };
  const serviceRef = useRef(null);
  const actions = useRef({});

  useEffect(() => {
    let active = true;
    setView({ scope, state: emptyState(), ready: false, enabled: false }); setError(null); setBusy(false);
    if (!scope) return undefined;
    let progressKey;
    const service = new NotificationService(client, scope,
      () => active && current.current.scope === scope && current.current.online,
      authUser.uid || authUser.firebase_uid, {
        start: () => {
          progressKey = `notifications-${Date.now()}-${Math.random()}`;
          notificationManager.startSync(progressKey, 'Obavijesti');
        },
        complete: success => notificationManager.completeSync(progressKey, success, success ? null : 'Obavijesti nisu sinhronizovane.'),
      });
    serviceRef.current = service;
    const reload = async () => {
      const state = await transact(scope);
      if (active) setView({ scope, state, ready: true, enabled: service.enabled !== false });
      return state;
    };
    const sync = async (manual = false) => {
      // Notification traffic is exclusively user-triggered, regardless of the
      // global auto-sync setting. Mount/focus/reconnect must never reach the API.
      if (!manual || !active) return;
      if (!current.current.online) throw new Error('Sinhronizacija zahtijeva internet vezu');
      setBusy(true);
      try {
        await service.sync(true, true);
        if (active) setError(null);
      } catch (e) {
        if (active) setError('Obavijesti nisu sinhronizovane. Sačuvani podaci su zadržani.');
        throw e;
      } finally {
        if (active) { await reload(); setBusy(false); refreshStatus(false); }
      }
    };
    actions.current = {
      sync, reload,
      close: async id => { await queueClose(scope, id); await reload(); refreshStatus(false); },
      history: async cursor => { const result = await service.history(cursor); await reload(); return result; },
      retry: async (id, cancel) => { await service.retry(id, cancel); await reload(); if (!cancel) await sync(true); },
    };
    const clearBridge = setNotificationBridge({
      sync: () => sync(true),
      pending: async () => Object.values((await transact(scope)).operations).map(op => ({ ...op, id: op.operationId, type: 'COMPLETE_NOTIFICATION' })),
    });
    reload().catch(() => { if (active) setError('Lokalni podaci obavijesti nisu dostupni.'); });
    // Refresh local state across PWA windows without broadcasting private data.
    const localTimer = setInterval(() => { if (!document.hidden) reload().catch(() => {}); }, 5000);
    return () => {
      active = false; service.dispose(); clearBridge();
      if (serviceRef.current === service) serviceRef.current = null;
      clearInterval(localTimer);
    };
    // Scope includes every identity/configuration value used by the service.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, client]);
  const valid = view.scope === scope && Boolean(scope);
  const state = valid ? view.state : emptyState();
  const call = name => (...args) => {
    if (!scope || serviceRef.current?.scope !== scope) return Promise.reject(new Error('Prijava je promijenjena'));
    return actions.current[name](...args);
  };
  return <Context.Provider value={{
    ownerScope: scope,
    enabled: valid && view.enabled, ready: !scope || (valid && view.ready), state, busy: valid && busy, error: valid ? error : null,
    online, count: Object.values(state.rows).filter(row => !row.completedAt && !state.operations[row.id]).length,
    sync: call('sync'), close: call('close'), history: call('history'), retry: call('retry'),
  }}>{children}</Context.Provider>;
}
